/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Checkbox as AntCheckbox } from 'antd'
import React from 'react'
import { ErrorLabel } from '../ErrorLabel'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import Text from '../Text'
import { SerializedStyles } from '@emotion/react'

/**
 * Checkbox component that combines Ant Design's Checkbox with custom styling and error handling.
 * Provides a consistent checkbox interface across the application with support for labels,
 * hints, and error states.
 *
 * @example
 * ```tsx
 * <Checkbox
 *   label="Accept terms and conditions"
 *   hint="Please read our terms before accepting"
 *   name="terms"
 *   value={accepted}
 *   onChange={(e) => setAccepted(e.target.checked)}
 * />
 * ```
 */
interface CheckboxProps {
  /** The label text or node to display next to the checkbox */
  label: string | React.ReactNode
  /** Optional hint text to display below the label */
  hint?: string
  /** Whether the checkbox is disabled */
  disabled?: boolean
  /** The name of the field (used for error handling) */
  name: string
  /** The current value of the checkbox */
  value: boolean
  /** Handler called when the checkbox value changes */
  onChange: (e: CheckboxChangeEvent) => void
  /** Array of field validation errors */
  fieldErrors?: any[]
  /** Custom error object */
  customError?: Record<string, any>
  /** Optional CSS styles to apply to the checkbox */
  checkBoxStyle?: SerializedStyles
  /** Use extra small font size for the label */
  extraSmallFont?: boolean
  /** Tab index for keyboard navigation */
  tabIndex?: number
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  disabled = false,
  name,
  value,
  onChange,
  fieldErrors = [],
  customError = {},
  checkBoxStyle,
  hint,
  extraSmallFont = false,
  tabIndex,
}) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: flex-start;
        gap: 8px;
      `}
    >
      <AntCheckbox
        disabled={disabled}
        onChange={onChange}
        checked={value}
        tabIndex={tabIndex}
        css={[
          css`
            width: 100%;
            max-width: 16px;
            align-self: center;
          `,
          checkBoxStyle,
        ]}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          flex: 1 0 0;
          width: 100%;
        `}
      >
        <Text
          variant={extraSmallFont ? 'paragraphXSmall' : 'paragraphSmall'}
          weight='regular'
          color={colors.WHITE}
        >
          {label}
        </Text>
        {hint && (
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_100}
          >
            {hint}
          </Text>
        )}
      </div>
      <ErrorLabel
        fieldErrors={fieldErrors}
        customError={customError}
        name={name}
      />
    </div>
  )
}

export default Checkbox
